import {useContext, useState} from "react";
import {DatePicker, Pagination, Select, Table} from "antd";
import dayjs from "dayjs";
import {
	Image,
	Button,
	StatusLabel,
	PopperUserInfo
} from "~/components";
import {
	renderDate,
	numberFormat,
	arrayToMap
} from "~/utils";

import RevenueAction from "./RevenueAction";
import {
	useDevice,
	useGroup
} from "~/hooks";
import {AppContext} from "~/context/AppProvider";

const paymentOptions = [
	{ value: 'cod', label: 'Tiền mặt'},
	{ value: 'bank', label: 'Chuyển khoản'},
	{ value: 'momo', label: 'Ví Momo'},
	{ value: 'swipe', label: 'Quẹt thẻ'},
	{ value: 'alePay', label: 'AlePay'},
];

function RevenueTable({items = [], filter, pagination, onPaginationChange, onChangeFilter, setItemEdit, openModal, handleStatus, can, onInlineUpdate}) {

	const {tableHeight} = useContext(AppContext);

	const {isMobile} = useDevice();

	const listGroup = useGroup();

	const groupMap = arrayToMap(listGroup);

	const [editingCell, setEditingCell] = useState(null); // { id, field }

	const canEditInline = can?.plus || can?.edit;

	const handleInfoClick = (item) => {
		setItemEdit(item);
		openModal('info');
	}

	const handleStatusClick = (item) => {
		setItemEdit(item);
		handleStatus(item);
	}

	const handleEditClick = (item) => {
		setItemEdit(item);
		openModal('addEdit');
	}

	const handlePayClick = (item) => {
		setItemEdit(item);
		openModal('pay');
	}

	const handleDeleteClick = (item) => {
		setItemEdit(item);
		openModal('delete');
	}

	const handleTransferClick = (item) => {
		setItemEdit(item);
		openModal('transfer');
	}

	const handleContractClick = (contractId) => {
		if (!onChangeFilter) return;
		const newFilter = {
			...filter,
			contractId: contractId,
			status: '',
			userId: '',
			service: '',
			page: 1,
		};
		onChangeFilter(newFilter);
	}

	const handleInlineSave = (id, field, value) => {
		setEditingCell(null);
		if (onInlineUpdate) {
			onInlineUpdate(id, field, value);
		}
	}

	let columns = [
		{ title: 'Hợp đồng', dataIndex: 'code', key: "code", width: '350px', render: (_, item) => (
			<div>
				<Button small onClick={() => handleContractClick(item.contractId)}><b style={{color : 'var(--primary)'}}>HD{item.contractId}</b></Button>
				 {item?.contract.name}
				<p>
					{item?.roomSales == 1 && <StatusLabel small type="green">Tính cho phòng</StatusLabel>}
					{item?.roomSales == 0 && <StatusLabel small type="yellow">Không Tính cho phòng</StatusLabel>}
					{item?.roomSales == 2 && <StatusLabel small type="red">Không Tính DS</StatusLabel>}
					{isMobile &&
						<>
							<StatusLabel small type={item.service.badge}>{item.service.label}</StatusLabel>
							&nbsp;<StatusLabel small type={item.service.badge}>{item.serviceType.label}</StatusLabel>
						</>
					}
				</p>
				{isMobile && <p className="mt-1" style={{fontSize:'12px', color:'#ccc'}}>Ngày thu {renderDate(item?.time)}</p>}
			</div>
		)},
		{ title: 'Nhân viên', dataIndex: 'user', key: "user", width: '220px', render: (_, item) => (
			<PopperUserInfo user={item.user}>
				<div className="d-flex gap align-items">
					<div className="avatar"><Image src={item.user?.avatar} /></div>
					<div>
						<b>{item.user?.firstname + ' ' + item.user?.lastname}</b>
						<p>{(item?.groupId !== 0) ? groupMap[item?.groupId]?.name : item.user?.username}</p>
					</div>
				</div>
			</PopperUserInfo>
		)},
		{title: 'Loại thu', dataIndex: 'service', key: 'service', render:(_, item) => (
			<div>
				<StatusLabel small type={item.service.badge}>{item.service.label}</StatusLabel>
				&nbsp;<StatusLabel small type={item.service.badge}>{item.serviceType.label}</StatusLabel>
			</div>
		)},
		{ title: 'Số tiền', dataIndex: 'price', key: "price", width: '180px', render: (_, item) => (
			<div>
				<p><b>{numberFormat(item?.price)}đ</b></p>
			</div>
		)},
		{ title: 'Hoa hồng', dataIndex: 'percent', key: "percent", width: '180px', render: (_, item) => (
			<div>
				<p><b>{numberFormat(item?.commission)}đ ({item?.percent}%)</b></p>
			</div>
		)},
		{ title: 'Lương', dataIndex: 'pay', key: "pay", width: '180px', render: (_, item) => (
			<div>
				{item?.plus === 'confirm' && <StatusLabel small type="green">{renderDate(item.timePlus, 'month')}</StatusLabel>}
				{item?.plus === 'pending' && <StatusLabel small type="red">Chưa tính</StatusLabel>}
			</div>
		)},
		{ title: 'Ngày thu', dataIndex: 'signing', key: "signing", width: '120px', render: (_, item) => (
			<div><p>{renderDate(item?.time)}</p></div>
		)},
		{ title: 'Ngày thực nhận', dataIndex: 'timeReal', key: "timeReal", width: '160px', render: (_, item) => {
			const isEditing = editingCell?.id === item.id && editingCell?.field === 'timeReal';
			if (canEditInline && isEditing) {
				return (
					<DatePicker
						autoFocus
						size="small"
						defaultValue={item.timeReal ? dayjs(item.timeReal * 1000) : null}
						format="DD/MM/YYYY"
						popupStyle={{zIndex: 10000}}
						onChange={(date) => {
							if (date) {
								handleInlineSave(item.id, 'timeReal', Math.round(date.valueOf() / 1000));
							} else {
								setEditingCell(null);
							}
						}}
						onBlur={() => setEditingCell(null)}
					/>
				);
			}
			return (
				<div
					style={canEditInline ? {cursor: 'pointer', minHeight: '24px'} : {}}
					title={canEditInline ? 'Nhấn để chỉnh sửa' : ''}
					onClick={() => canEditInline && setEditingCell({id: item.id, field: 'timeReal'})}
				>
					{item.timeReal ? renderDate(item.timeReal) : <span style={{color: '#ccc'}}>--</span>}
				</div>
			);
		}},
		{ title: 'Thanh toán', dataIndex: 'payment', key: "payment", width: '150px', render: (_, item) => {
			const isEditing = editingCell?.id === item.id && editingCell?.field === 'payment';
			if (canEditInline && isEditing) {
				return (
					<Select
						autoFocus
						size="small"
						defaultValue={item.payment}
						options={paymentOptions}
						style={{minWidth: 130}}
						onChange={(value) => handleInlineSave(item.id, 'payment', value)}
						onBlur={() => setEditingCell(null)}
					/>
				);
			}
			const payOpt = paymentOptions.find(o => o.value === item.payment);
			return (
				<div
					style={canEditInline ? {cursor: 'pointer', minHeight: '24px'} : {}}
					title={canEditInline ? 'Nhấn để chỉnh sửa' : ''}
					onClick={() => canEditInline && setEditingCell({id: item.id, field: 'payment'})}
				>
					{payOpt ? payOpt.label : (item.payment || '--')}
				</div>
			);
		}},
		{title: 'Trạng thái', dataIndex: 'status', key: "status", width: '130px', render: (_, item) => (
			<StatusLabel small type={item.status.badge}>{item.status.label}</StatusLabel>
		)},
		{ title: '#', dataIndex: 'action', key: "action", width: '150px', render: (_, item) => (
			<RevenueAction
				key={item.id}
				item={item}
				onClickInfo={handleInfoClick}
				onClickStatus={handleStatusClick}
				onClickPay={handlePayClick}
				onClickEdit={handleEditClick}
				onClickDelete={handleDeleteClick}
				onClickTransfer={handleTransferClick}
			/>
		)},
	];

	if(isMobile) {
		columns = columns.filter(function (currentValue) {
			return (currentValue.key !== 'service' && currentValue.key !== 'signing')
		})
	}

	const dataSource= items.map(row => ({
		key: row.id,
		...row
	}));

	return (
		<div className="table-wrapper">
			<div className="table-box">
				<Table columns={columns} dataSource={dataSource} scroll={{ y:tableHeight, x: 'max-content' }} pagination={false} />
			</div>
			<Pagination current={pagination.page} defaultCurrent={1} defaultPageSize={20} pageSize={pagination.limit} total={pagination.total} onChange={onPaginationChange}  showSizeChanger={false} />
		</div>
	)
}

export default RevenueTable;